/**
 * Thin route wrappers around CardPage.  Each one:
 *   1. Reads its URL params via useParams()
 *   2. Synthesizes a question that the existing router/dispatcher
 *      pipeline can answer
 *   3. Renders <CardPage question=... />
 *
 * Keeping all the route wrappers in a single file (instead of one
 * file per route) is deliberate — they're trivial and live close
 * together logically.  When a wrapper grows past ~20 lines it earns
 * its own file.
 *
 * A3 of Phase A — Shareable Card URLs.  Three routes land here as
 * the smallest end-to-end slice; A6 adds the remaining 10+.
 */
import { useParams } from "react-router-dom";
import CardPage from "./CardPage";
import {
  urlSlugToPokemonName,
  leagueDisplayName,
  isStandardLeague,
  parseIvSlug,
  formatIvTriple,
  parseMoveComparisonSlug,
  parseComparisonSlug,
  urlSlugToMoveName,
  seasonSlugToTitle,
} from "../lib/urlParams";
import type { League } from "../types";

/** /p/:pokemon → base_stats card.  E.g. /p/swampert */
export function PokemonStatsPage() {
  const { pokemon = "" } = useParams<{ pokemon: string }>();
  const name = urlSlugToPokemonName(pokemon);
  // Mirrors the canonical wording from PromptsSheet's "Tell me about X"
  // family so the router's few-shots route reliably to base_stats.
  const question = `Tell me about ${name}.`;
  return <CardPage question={question} fetchKey={`stats:${pokemon}`} />;
}

/** /p/:pokemon/:league → league_ranking card.  E.g. /p/swampert/great
 *
 * Guarded against non-league second segments via isStandardLeague():
 * future routes like /p/swampert/moves and /p/swampert/evolution
 * share the same URL shape, so the router config below uses this
 * component only after the static-segment routes have first crack.
 */
export function PokemonLeaguePage() {
  const { pokemon = "", league = "" } = useParams<{ pokemon: string; league: string }>();
  const name = urlSlugToPokemonName(pokemon);
  const leagueName = leagueDisplayName(league);
  // Defensive: if someone hand-crafts /p/foo/notaleague we still send
  // it through — the dispatcher will fall through to general / not-found
  // and CardPage renders that cleanly.  No need to 404 here.
  const guardedLeague = isStandardLeague(league) ? league : null;
  const question = `How does ${name} rank in ${leagueName} League?`;
  return (
    <CardPage
      question={question}
      league={guardedLeague}
      fetchKey={`league:${pokemon}:${league}`}
    />
  );
}

/** /lore/:pokemon → lore_general card.  E.g. /lore/eevee */
export function PokemonLorePage() {
  const { pokemon = "" } = useParams<{ pokemon: string }>();
  const name = urlSlugToPokemonName(pokemon);
  // "Tell me about X's biology" matches the lore_general intent
  // few-shots better than just "Tell me about X" (which would route
  // to base_stats and emit a pokemon_card, not a lore_card).
  const question = `Tell me the lore and biology of ${name}.`;
  return <CardPage question={question} fetchKey={`lore:${pokemon}`} />;
}

// ---------------------------------------------------------------------------
// A6 — remaining evergreen /p/<pokemon>/<seg> routes.
// All synthesize natural-language questions that match the router's
// existing few-shot examples, so we never need to add new router
// intents or examples to support these URLs.
// ---------------------------------------------------------------------------

/** /p/:pokemon/moves → move_list card. */
export function PokemonMovesPage() {
  const { pokemon = "" } = useParams<{ pokemon: string }>();
  const name = urlSlugToPokemonName(pokemon);
  return (
    <CardPage
      question={`What moves can ${name} learn?`}
      fetchKey={`moves:${pokemon}`}
    />
  );
}

/** /p/:pokemon/evolution → evolution_info card. */
export function PokemonEvolutionPage() {
  const { pokemon = "" } = useParams<{ pokemon: string }>();
  const name = urlSlugToPokemonName(pokemon);
  return (
    <CardPage
      question={`How does ${name} evolve?`}
      fetchKey={`evo:${pokemon}`}
    />
  );
}

/** /p/:pokemon/candy → candy_cost card. */
export function PokemonCandyPage() {
  const { pokemon = "" } = useParams<{ pokemon: string }>();
  const name = urlSlugToPokemonName(pokemon);
  return (
    <CardPage
      question={`How much candy does ${name} cost to evolve and power up?`}
      fetchKey={`candy:${pokemon}`}
    />
  );
}

/** /p/:pokemon/counters → raid_counter card. */
export function PokemonCountersPage() {
  const { pokemon = "" } = useParams<{ pokemon: string }>();
  const name = urlSlugToPokemonName(pokemon);
  return (
    <CardPage
      question={`Who counters ${name} in raids?`}
      fetchKey={`counters:${pokemon}`}
    />
  );
}

/** /p/:pokemon/best-ivs → best_ivs card (all leagues). */
export function PokemonBestIvsPage() {
  const { pokemon = "" } = useParams<{ pokemon: string }>();
  const name = urlSlugToPokemonName(pokemon);
  return (
    <CardPage
      question={`What are the best IVs for ${name}?`}
      fetchKey={`bestivs:${pokemon}`}
    />
  );
}

/** /p/:pokemon/elite-tm → elite_tm_info card. */
export function PokemonEliteTmPage() {
  const { pokemon = "" } = useParams<{ pokemon: string }>();
  const name = urlSlugToPokemonName(pokemon);
  return (
    <CardPage
      question={`What Elite TM moves does ${name} have?`}
      fetchKey={`elitetm:${pokemon}`}
    />
  );
}

/** /p/:pokemon/iv/:ivs → iv_ranking card.  E.g. /p/swampert/iv/0-13-14 */
export function PokemonIvRankingPage() {
  const { pokemon = "", ivs = "" } = useParams<{ pokemon: string; ivs: string }>();
  const name = urlSlugToPokemonName(pokemon);
  const triple = parseIvSlug(ivs);
  // Defensive: if IVs are malformed (e.g. /p/foo/iv/abc), still send
  // through — the dispatcher will fall through to a friendly error and
  // CardPage's error state will catch it.
  const ivLabel = triple ? formatIvTriple(triple) : ivs;
  return (
    <CardPage
      question={`What rank is my ${ivLabel} ${name}?`}
      fetchKey={`iv:${pokemon}:${ivs}`}
    />
  );
}

/** /p/:pokemon/xl/:league → xl_candy card.  E.g. /p/swampert/xl/great */
export function PokemonXlCandyPage() {
  const { pokemon = "", league = "" } = useParams<{ pokemon: string; league: string }>();
  const name = urlSlugToPokemonName(pokemon);
  const guardedLeague: League | null = isStandardLeague(league) ? league : null;
  const leagueName = leagueDisplayName(league);
  return (
    <CardPage
      question={`Does ${name} need XL Candy in ${leagueName} League?`}
      league={guardedLeague}
      fetchKey={`xl:${pokemon}:${league}`}
    />
  );
}

// ---------------------------------------------------------------------------
// A6 — /m/, /compare/, /league/ routes
// ---------------------------------------------------------------------------

/** /m/:move → move_detail card.  OR /m/:a-vs-:b → move_comparison.
 *
 * Dispatch is by slug content: anything containing "-vs-" is treated as
 * a comparison, otherwise it's a single-move detail query.  This lets
 * us register a single route component for both URL shapes.
 */
export function MoveOrComparisonPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const pair = parseMoveComparisonSlug(slug);
  if (pair) {
    return (
      <CardPage
        question={`Compare ${pair[0]} vs ${pair[1]}`}
        fetchKey={`movecmp:${slug}`}
      />
    );
  }
  const moveName = urlSlugToMoveName(slug);
  return (
    <CardPage
      question={`Tell me about ${moveName}`}
      fetchKey={`movedetail:${slug}`}
    />
  );
}

/** /compare/:names → comparison card.  E.g. /compare/garchomp-vs-dragonite */
export function ComparisonPage() {
  const { names = "" } = useParams<{ names: string }>();
  const list = parseComparisonSlug(names);
  if (list.length < 2) {
    return (
      <CardPage
        question={`Tell me about ${list[0] ?? "this Pokémon"}.`}
        fetchKey={`cmp-fallback:${names}`}
      />
    );
  }
  const question = `Compare ${list.join(" vs ")}.`;
  return <CardPage question={question} fetchKey={`cmp:${names}`} />;
}

/** /league/:league/top → meta_question card. */
export function LeagueTopPage() {
  const { league = "" } = useParams<{ league: string }>();
  const guardedLeague: League | null = isStandardLeague(league) ? league : null;
  const leagueName = leagueDisplayName(league);
  return (
    <CardPage
      question={`Who are the top Pokémon in ${leagueName} League?`}
      league={guardedLeague}
      fetchKey={`top:${league}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Phase B — season share URLs.
//
// All five season-intent routes accept an OPTIONAL :slug path segment
// that pins the backend lookup to a specific gbl_season row.  When
// :slug is omitted (e.g. /season vs /season/forever-forward) the
// backend falls back to "latest" semantics, so the URL is still
// meaningful — it just means "whatever the current season is."
//
// The slug is forwarded to /ask via seasonSlug; the route component
// also references it in the synthesized question text so the router
// classifies correctly (season_summary, cup_schedule, etc.).
// ---------------------------------------------------------------------------

/** /season  OR  /season/:slug  →  season_summary card.
 *
 * /season alone → whatever's currently latest (no slug-pin).
 * /season/<slug> → that specific season, even after a newer one ships.
 */
export function SeasonSummaryPage() {
  const { slug = "" } = useParams<{ slug?: string }>();
  const title = slug ? seasonSlugToTitle(slug) : "";
  // When a slug is pinned, name it in the question so the router
  // routes to season_summary cleanly.  Without a slug, fall back to
  // the canonical "what's new this season" phrasing.
  const question = slug
    ? `Tell me about the ${title} GBL season.`
    : `What's new in the GBL season?`;
  return (
    <CardPage
      question={question}
      seasonSlug={slug || null}
      fetchKey={`season:${slug || "current"}`}
    />
  );
}

/** /season/:slug/cups → cup_schedule card. */
export function SeasonCupsPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const title = seasonSlugToTitle(slug);
  return (
    <CardPage
      question={`What's the cup schedule for the ${title} GBL season?`}
      seasonSlug={slug}
      fetchKey={`season-cups:${slug}`}
    />
  );
}

/** /season/:slug/move-changes → move_change card (all changes). */
export function SeasonMoveChangesPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const title = seasonSlugToTitle(slug);
  return (
    <CardPage
      question={`What move balance changes did the ${title} GBL season bring?`}
      seasonSlug={slug}
      fetchKey={`season-moves:${slug}`}
    />
  );
}

/** /season/:slug/new-moves → new_moves_learned card. */
export function SeasonNewMovesPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const title = seasonSlugToTitle(slug);
  return (
    <CardPage
      question={`What new moves were added in the ${title} GBL season?`}
      seasonSlug={slug}
      fetchKey={`season-newmoves:${slug}`}
    />
  );
}

/** /season/:slug/cup/:cupSlug → cup_rules card for a specific cup. */
export function SeasonCupRulesPage() {
  const { slug = "", cupSlug = "" } = useParams<{ slug: string; cupSlug: string }>();
  const cupTitle = seasonSlugToTitle(cupSlug);
  // The cup name in the question text is what the dispatcher's fuzzy
  // matcher (cup_filter) keys off to find the right cup_schedule entry
  // inside the season's row.  Both pieces matter:
  //   - seasonSlug pins which gbl_season row to load
  //   - cupTitle in the question identifies which cup within that season
  return (
    <CardPage
      question={`What are the rules for the ${cupTitle} cup?`}
      seasonSlug={slug}
      fetchKey={`season-cup:${slug}:${cupSlug}`}
    />
  );
}
